import { inject, Injectable } from '@angular/core';
import { EntityState } from '@ngrx/entity';

import {
  CanvasElement,
  CanvasElementId,
  CanvasElementType,
  findByRefIds,
  FormOutputUpdateParams,
  PartialPropsOf,
  setWrapperRefMutably,
  WrapperRefs,
} from '@blaze-runtime/engine-domain';
import { deepmergeExceptArrays } from '@blaze-shared/shared-util';
import { StyleSheet } from '@blaze-shared/theming-domain';

import {
  hasDirtyFieldProps,
  hasProps,
} from '../../../canvas-elements/element-model/element-props-guards';
import { FormFieldGenerationParams } from '../../../elements/__base/generation';
import { transformElementTypeMutably } from '../../../utils/canvas-element/transform';
import {
  CreateCanvasElementModel,
  FindFieldTargetPathData,
} from '../../../utils/elements/generation.utils';
import { placeElementMutably } from '../../../utils/layout/place-element';
import { ElementDefinitionsService } from '../../element-definitions.service';
import { ElementTransformationService } from '../../element-transformation.service';
import { makeStyleConfigPropsForDefaultStyleSheet } from '../../styles';

import { getFieldTargetPath } from './form-field-placement';
import { type FormLikeElement } from './form-generation-data';

/** Creates and updates the input elements that stand for a form's fields. */
@Injectable({ providedIn: 'root' })
export class FormFieldGenerationService {
  private readonly elementDefinitions = inject(ElementDefinitionsService);
  private readonly elementTransformationService = inject(ElementTransformationService);

  /**
   * Add or update field in the form
   * @param entityState the entity state
   * @param refs the form's wrapper refs, which is where the field's element id is tracked
   * @param fieldData the element to create, and the props to initialize or update it with
   * @param findFieldTargetPathData data used to find the target path for the new element
   * @returns params needed to update the form output in case of element type transformation; otherwise undefined
   */
  addOrUpdateFieldMutably(
    entityState: EntityState<CanvasElement>,
    refs: WrapperRefs,
    fieldData: CreateCanvasElementModel,
    findFieldTargetPathData: FindFieldTargetPathData<FormLikeElement>,
  ): FormOutputUpdateParams | undefined {
    const elementId =
      findByRefIds(refs, fieldData.refIds) ||
      this.createFieldMutably(entityState, refs, fieldData, findFieldTargetPathData);

    const element = entityState.entities[elementId];
    if (!element) {
      throw new Error('Field element error');
    }

    const formUpdateParams =
      element.type !== fieldData.elementDefinition.type
        ? this.transformFieldMutably(entityState, element, fieldData)
        : undefined;

    if (hasProps(element) && fieldData.updateProps) {
      element.props = deepmergeExceptArrays(element.props as any, fieldData.updateProps as any);
      forgetCleanedUpDirtyFlagsMutably(element, fieldData);
    }

    return formUpdateParams;
  }

  /**
   * Builds the props a generated field starts with, letting the element's own definition
   * decide what a form field of its type looks like.
   */
  initializeGeneratedFormFieldProps<T extends CanvasElementType>(
    type: T,
    params: FormFieldGenerationParams<T>,
    styleSheets: StyleSheet[],
  ): PartialPropsOf<T> | undefined {
    const definition = this.elementDefinitions.getDefinition(type);
    if (!definition) {
      return undefined;
    }

    /**
     * TODO: [MG] replace with setStyleSheetToElementMutably
     * Currently blocked due to complexity of the solution
     */
    const props = definition.generation?.formField?.({ ...params }) ?? params.props;
    return {
      ...props,
      ...makeStyleConfigPropsForDefaultStyleSheet(styleSheets, type),
    };
  }

  private createFieldMutably(
    entityState: EntityState<CanvasElement>,
    refs: WrapperRefs,
    fieldData: CreateCanvasElementModel,
    findFieldTargetPathData: FindFieldTargetPathData<FormLikeElement>,
  ): CanvasElementId {
    const result = placeElementMutably(entityState, {
      elementDefinition: fieldData.elementDefinition,
      initProps: fieldData.initProps,
      atPath: getFieldTargetPath(entityState, findFieldTargetPathData),
    });
    if (!result || !result.elementId) {
      throw new Error(`Field element creation error`);
    }

    setWrapperRefMutably(refs, fieldData.refIds, result.elementId);
    return result.elementId;
  }

  /** Retypes an existing field, e.g. when the author picks another control for a column. */
  private transformFieldMutably(
    entityState: EntityState<CanvasElement>,
    element: CanvasElement,
    fieldData: CreateCanvasElementModel,
  ): FormOutputUpdateParams {
    const targetType = fieldData.elementDefinition.type;
    const transformationData = this.elementTransformationService.getTransformation(
      element.type,
      targetType,
    );
    if (!transformationData || !transformationData.transformElementFn) {
      console.warn(`Transformation data missing for ${element.type}->${targetType}`);
    }

    const formUpdateParams: FormOutputUpdateParams = {
      elementId: element.id,
      sourceType: element.type,
      targetType,
    };
    transformElementTypeMutably(
      entityState,
      element.id,
      fieldData.elementDefinition,
      transformationData?.transformElementFn ?? ((unchanged): CanvasElement => unchanged),
      fieldData.initProps,
      fieldData.updateProps,
    );

    return formUpdateParams;
  }
}

/**
 * Drops the dirty flags that no longer describe the field: a label the author had edited
 * stops counting as edited once the generated label matches it again.
 */
function forgetCleanedUpDirtyFlagsMutably(
  element: CanvasElement,
  fieldData: CreateCanvasElementModel,
): void {
  if (!hasDirtyFieldProps(element) || !element.props.dirty) {
    return;
  }

  const { dirty } = element.props;
  if (dirty.label && 'content' in element.props && 'title' in element.props.content) {
    if (element.props.content.title.label === fieldData.inputData.title) {
      delete dirty.label;
    }
  }
  Object.entries(dirty).forEach(([key, value]) => {
    if (value === undefined) {
      delete (dirty as any)[key];
    }
  });
  if (!Object.keys(dirty).length) {
    delete element.props.dirty;
  }
}
